import { useState, useEffect, useRef, useCallback } from 'react';
import type { Map } from 'mapbox-gl';
import { BeatAPIZeppelinRepository } from 'ts-core';
import type { Zeppelin } from 'ts-core';
import { useApi, useTranslations } from 'react-ui';

const DEBOUNCE_MS = 800;

interface UseZeppelinsV2Return {
  zeppelins: Zeppelin[];
  loading: boolean;
  error: Error | null;
}

/**
 * Fetches v2 zeppelins from GET /api/v2/zeppelins on each map move/zoom or
 * when the active tag filter changes.
 * Debounced at 800 ms to avoid excessive API traffic.
 *
 * @param map - The Mapbox GL map instance.
 * @param tags - Active tag slug filters from the query state. When non-empty,
 *               only zeppelins tagged with any of those slugs are returned.
 */
export function useZeppelinsV2(map: Map | null, tags: string[] = []): UseZeppelinsV2Return {
  const { apiEndpoints, apiKey } = useApi();
  const { language } = useTranslations();

  const [zeppelins, setZeppelins] = useState<Zeppelin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const repositoryRef = useRef<BeatAPIZeppelinRepository>(
    new BeatAPIZeppelinRepository(apiEndpoints.zeppelins, apiKey ?? '')
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageRef = useRef(language);
  const tagsRef = useRef(tags);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  useEffect(() => {
    repositoryRef.current.url = apiEndpoints.zeppelins;
  }, [apiEndpoints.zeppelins]);

  useEffect(() => {
    if (apiKey != null) {
      repositoryRef.current.apiKey = apiKey;
    }
  }, [apiKey]);

  const fetchZeppelins = useCallback(async (mapInstance: Map) => {
    const bounds = mapInstance.getBounds();
    if (!bounds) return;

    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();

    setLoading(true);
    setError(null);
    try {
      const data = await repositoryRef.current.getZeppelins({
        top_left: [nw.lat, nw.lng],
        bottom_right: [se.lat, se.lng],
        locale: languageRef.current,
        tags: tagsRef.current.length > 0 ? tagsRef.current : undefined,
      });
      setZeppelins([...data].sort((a, b) => a.priority - b.priority));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch immediately when tags change (no debounce needed — user action)
  useEffect(() => {
    if (!map) return;
    fetchZeppelins(map);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, map, fetchZeppelins]);

  useEffect(() => {
    if (!map) return;

    const handler = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchZeppelins(map);
      }, DEBOUNCE_MS);
    };

    map.on('moveend', handler);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      map.off('moveend', handler);
    };
  }, [map, fetchZeppelins]);

  return { zeppelins, loading, error };
}
